import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Lightbulb, Zap, Target, BookOpen, Layers } from 'lucide-react';
import type { MavisOpportunity, OpportunityMotion, CustomerRelationship } from '../../types/mavis';
import {
  OPPORTUNITY_MOTION_LABELS, CUSTOMER_RELATIONSHIP_LABELS,
} from '../../types/mavis';
import type { Hypothesis, UseCaseItem, AccountTrigger } from '../../types';

interface AccountSummary {
  id: string;
  company_name: string;
  industry: string | null;
  tier: string | null;
  website_url: string | null;
}

interface Props {
  opportunity: MavisOpportunity;
  account: AccountSummary | null;
  hypothesis: Hypothesis | null;
  useCases: UseCaseItem[];
  triggers: AccountTrigger[];
  saving: boolean;
  onPatch: (patch: Record<string, unknown>) => Promise<void>;
}

const OPPORTUNITY_MOTIONS: OpportunityMotion[] = [
  'new_business_land', 'renewal', 'upsell', 'cross_sell', 'expansion',
  'competitive_displacement', 'partner_led', 'save_risk', 'unknown',
];
const CUSTOMER_RELATIONSHIPS: CustomerRelationship[] = [
  'net_new', 'existing_customer', 'former_customer', 'partner_influenced', 'unknown',
];

const textAreaClass =
  'w-full text-sm text-app-ink bg-transparent border-0 focus:outline-none resize-none ' +
  'placeholder:text-app-muted leading-relaxed';

export default function MavisOverviewPanel({
  opportunity, account, hypothesis, useCases, triggers, saving, onPatch,
}: Props) {
  const [description, setDescription] = useState(opportunity.description);
  const [reasoning, setReasoning] = useState(opportunity.forecast_reasoning);

  // Deal context local state
  const [motion, setMotion]       = useState<OpportunityMotion>(opportunity.opportunity_motion ?? 'unknown');
  const [relationship, setRel]    = useState<CustomerRelationship>(opportunity.customer_relationship ?? 'unknown');
  const [incumbent, setIncumbent] = useState(opportunity.incumbent_vendor ?? '');
  const [competitor, setComp]     = useState(opportunity.competitor_in_play ?? '');
  const [currentProds, setCurrProds] = useState((opportunity.current_products ?? []).join(', '));
  const [targetProds, setTargProds]  = useState((opportunity.target_products ?? []).join(', '));
  const [renewalDate, setRenewalDate] = useState(opportunity.renewal_date ?? '');
  const [contractRisk, setContractRisk] = useState(opportunity.contract_risk ?? '');

  // Sync description when navigating between opportunities
  useEffect(() => {
    setDescription(opportunity.description);
    setMotion(opportunity.opportunity_motion ?? 'unknown');
    setRel(opportunity.customer_relationship ?? 'unknown');
    setIncumbent(opportunity.incumbent_vendor ?? '');
    setComp(opportunity.competitor_in_play ?? '');
    setCurrProds((opportunity.current_products ?? []).join(', '));
    setTargProds((opportunity.target_products ?? []).join(', '));
    setRenewalDate(opportunity.renewal_date ?? '');
    setContractRisk(opportunity.contract_risk ?? '');
  }, [opportunity.id]);

  // Sync reasoning separately so that applying a forecast from the Assessment tab
  // (which updates opportunity.forecast_reasoning in parent state without changing id)
  // is reflected immediately in this textarea.
  useEffect(() => {
    setReasoning(opportunity.forecast_reasoning);
  }, [opportunity.forecast_reasoning]);

  function splitProducts(val: string): string[] {
    return val.split(',').map(v => v.trim()).filter(Boolean);
  }

  const topUseCases = useCases.slice(0, 2);
  const topTriggers = triggers.slice(0, 3);
  const hasContext = hypothesis !== null || topUseCases.length > 0 || topTriggers.length > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

      {/* ── Left column: editable deal text ──────────────────────────── */}
      <div className="lg:col-span-5 space-y-4">

        {/* Description */}
        <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm">
          <div className="px-5 py-3.5 border-b border-app-line flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-app-muted" />
            <h3 className="text-sm font-semibold text-app-ink">Description</h3>
          </div>
          <div className="px-5 py-4">
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              onBlur={() => {
                if (description !== opportunity.description) {
                  onPatch({ description });
                }
              }}
              disabled={saving}
              rows={6}
              className={`${textAreaClass} disabled:opacity-60`}
              placeholder="Add a description — deal context, background, key stakeholders…"
            />
          </div>
        </div>

        {/* Forecast reasoning */}
        <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm">
          <div className="px-5 py-3.5 border-b border-app-line flex items-center gap-2">
            <Target className="w-4 h-4 text-app-muted" />
            <h3 className="text-sm font-semibold text-app-ink">Forecast Reasoning</h3>
          </div>
          <div className="px-5 py-4">
            <textarea
              value={reasoning}
              onChange={e => setReasoning(e.target.value)}
              onBlur={() => {
                if (reasoning !== opportunity.forecast_reasoning) {
                  onPatch({ forecast_reasoning: reasoning });
                }
              }}
              disabled={saving}
              rows={4}
              className={`${textAreaClass} disabled:opacity-60`}
              placeholder="Why is this deal forecasted at the current label / %?"
            />
          </div>
        </div>
        {/* Deal Context */}
        <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm">
          <div className="px-5 py-3.5 border-b border-app-line flex items-center gap-2">
            <Layers className="w-4 h-4 text-indigo-400" />
            <h3 className="text-sm font-semibold text-app-ink">Deal Context</h3>
          </div>
          <div className="px-5 py-4 grid grid-cols-2 gap-x-4 gap-y-3.5">
            {/* Motion */}
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-[10px] font-semibold text-app-muted uppercase tracking-wider mb-1">
                Motion
              </label>
              <select
                value={motion}
                onChange={async e => {
                  const v = e.target.value as OpportunityMotion;
                  setMotion(v);
                  await onPatch({ opportunity_motion: v });
                }}
                disabled={saving}
                className="w-full text-xs text-app-ink bg-transparent border border-app-line rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-forest-400 disabled:opacity-60"
              >
                {OPPORTUNITY_MOTIONS.map(m => (
                  <option key={m} value={m}>{OPPORTUNITY_MOTION_LABELS[m]}</option>
                ))}
              </select>
            </div>

            {/* Relationship */}
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-[10px] font-semibold text-app-muted uppercase tracking-wider mb-1">
                Customer Relationship
              </label>
              <select
                value={relationship}
                onChange={async e => {
                  const v = e.target.value as CustomerRelationship;
                  setRel(v);
                  await onPatch({ customer_relationship: v });
                }}
                disabled={saving}
                className="w-full text-xs text-app-ink bg-transparent border border-app-line rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-forest-400 disabled:opacity-60"
              >
                {CUSTOMER_RELATIONSHIPS.map(r => (
                  <option key={r} value={r}>{CUSTOMER_RELATIONSHIP_LABELS[r]}</option>
                ))}
              </select>
            </div>

            {/* Incumbent */}
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-[10px] font-semibold text-app-muted uppercase tracking-wider mb-1">
                Incumbent Vendor
              </label>
              <input
                type="text"
                value={incumbent}
                onChange={e => setIncumbent(e.target.value)}
                onBlur={() => {
                  if (incumbent !== (opportunity.incumbent_vendor ?? '')) {
                    onPatch({ incumbent_vendor: incumbent.trim() || null });
                  }
                }}
                disabled={saving}
                placeholder="e.g. Salesforce"
                className="w-full text-xs text-app-ink bg-transparent border border-app-line rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-forest-400 disabled:opacity-60 placeholder:text-app-muted"
              />
            </div>

            {/* Competitor */}
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-[10px] font-semibold text-app-muted uppercase tracking-wider mb-1">
                Competitor in Play
              </label>
              <input
                type="text"
                value={competitor}
                onChange={e => setComp(e.target.value)}
                onBlur={() => {
                  if (competitor !== (opportunity.competitor_in_play ?? '')) {
                    onPatch({ competitor_in_play: competitor.trim() || null });
                  }
                }}
                disabled={saving}
                placeholder="e.g. HubSpot"
                className="w-full text-xs text-app-ink bg-transparent border border-app-line rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-forest-400 disabled:opacity-60 placeholder:text-app-muted"
              />
            </div>

            {/* Current products */}
            <div className="col-span-2">
              <label className="block text-[10px] font-semibold text-app-muted uppercase tracking-wider mb-1">
                Current Products <span className="font-normal normal-case text-app-muted">(comma-separated)</span>
              </label>
              <input
                type="text"
                value={currentProds}
                onChange={e => setCurrProds(e.target.value)}
                onBlur={() => {
                  const arr = splitProducts(currentProds);
                  onPatch({ current_products: arr });
                }}
                disabled={saving}
                placeholder="e.g. CRM Core, Analytics"
                className="w-full text-xs text-app-ink bg-transparent border border-app-line rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-forest-400 disabled:opacity-60 placeholder:text-app-muted"
              />
            </div>

            {/* Target products */}
            <div className="col-span-2">
              <label className="block text-[10px] font-semibold text-app-muted uppercase tracking-wider mb-1">
                Target Products <span className="font-normal normal-case text-app-muted">(comma-separated)</span>
              </label>
              <input
                type="text"
                value={targetProds}
                onChange={e => setTargProds(e.target.value)}
                onBlur={() => {
                  const arr = splitProducts(targetProds);
                  onPatch({ target_products: arr });
                }}
                disabled={saving}
                placeholder="e.g. Enterprise Suite, AI Add-on"
                className="w-full text-xs text-app-ink bg-transparent border border-app-line rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-forest-400 disabled:opacity-60 placeholder:text-app-muted"
              />
            </div>

            {/* Renewal date */}
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-[10px] font-semibold text-app-muted uppercase tracking-wider mb-1">
                Renewal Date
              </label>
              <input
                type="date"
                value={renewalDate}
                onChange={e => setRenewalDate(e.target.value)}
                onBlur={() => {
                  if (renewalDate !== (opportunity.renewal_date ?? '')) {
                    onPatch({ renewal_date: renewalDate || null });
                  }
                }}
                disabled={saving}
                className="w-full text-xs text-app-ink bg-transparent border border-app-line rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-forest-400 disabled:opacity-60"
              />
            </div>

            {/* Contract risk */}
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-[10px] font-semibold text-app-muted uppercase tracking-wider mb-1">
                Contract Risk
              </label>
              <input
                type="text"
                value={contractRisk}
                onChange={e => setContractRisk(e.target.value)}
                onBlur={() => {
                  if (contractRisk !== (opportunity.contract_risk ?? '')) {
                    onPatch({ contract_risk: contractRisk.trim() || null });
                  }
                }}
                disabled={saving}
                placeholder="e.g. renewal at risk — low adoption"
                className="w-full text-xs text-app-ink bg-transparent border border-app-line rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-forest-400 disabled:opacity-60 placeholder:text-app-muted"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Right column: read-only Heymakr context ──────────────────── */}
      <div className="lg:col-span-7 space-y-4">

        {/* Account info */}
        {account && (
          <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm">
            <div className="px-5 py-3.5 border-b border-app-line flex items-center gap-2">
              <Building2 className="w-4 h-4 text-app-muted" />
              <h3 className="text-sm font-semibold text-app-ink">Account</h3>
            </div>
            <div className="px-5 py-4">
              <Link
                to={`/app/accounts/${account.id}`}
                className="text-sm font-semibold text-forest-700 hover:text-forest-800 hover:underline"
              >
                {account.company_name}
              </Link>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                {account.industry && (
                  <span className="text-xs text-app-muted">{account.industry}</span>
                )}
                {account.tier && (
                  <>
                    {account.industry && <span className="text-app-muted">·</span>}
                    <span className="text-xs text-app-muted">Tier {account.tier}</span>
                  </>
                )}
                {account.website_url && (
                  <>
                    <span className="text-app-muted">·</span>
                    <a
                      href={account.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-forest-600 hover:underline"
                    >
                      Website ↗
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Selected hypothesis */}
        {hypothesis ? (
          <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm">
            <div className="px-5 py-3.5 border-b border-app-line flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-amber-500" />
              <h3 className="text-sm font-semibold text-app-ink">Selected Hypothesis</h3>
              <span className={`ml-auto px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-full ${
                hypothesis.rating === 'strong'
                  ? 'bg-forest-50 text-forest-700'
                  : hypothesis.rating === 'medium'
                  ? 'bg-amber-50 text-amber-700'
                  : 'bg-app-canvas text-app-muted'
              }`}>
                {hypothesis.rating}
              </span>
            </div>
            <div className="px-5 py-4 space-y-2">
              <p className="text-sm font-semibold text-app-ink">{hypothesis.title}</p>
              {hypothesis.one_liner && (
                <p className="text-xs text-app-muted italic">"{hypothesis.one_liner}"</p>
              )}
              {hypothesis.impact && (
                <p className="text-xs text-app-muted leading-relaxed">{hypothesis.impact}</p>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm">
            <div className="px-5 py-3.5 border-b border-app-line flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-app-muted" />
              <h3 className="text-sm font-semibold text-app-muted">Hypothesis</h3>
            </div>
            <div className="px-5 py-4">
              <p className="text-xs text-app-muted">
                No hypothesis selected for this account.{' '}
                {account && (
                  <Link
                    to={`/app/accounts/${account.id}`}
                    className="text-forest-600 hover:underline"
                  >
                    Run account research
                  </Link>
                )}{' '}
                to generate and select one.
              </p>
            </div>
          </div>
        )}

        {/* Top 2 use cases */}
        {topUseCases.length > 0 && (
          <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm">
            <div className="px-5 py-3.5 border-b border-app-line flex items-center gap-2">
              <Target className="w-4 h-4 text-indigo-500" />
              <h3 className="text-sm font-semibold text-app-ink">Top Use Cases</h3>
            </div>
            <div className="divide-y divide-app-line">
              {topUseCases.map((uc, i) => (
                <div key={i} className="px-5 py-3.5">
                  <div className="flex items-start gap-2.5">
                    <span className="text-xs font-bold text-app-muted mt-0.5 flex-shrink-0">
                      #{uc.rank ?? i + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-app-ink">{uc.title}</p>
                      {uc.sales_hook && (
                        <p className="text-xs text-app-muted mt-0.5 leading-relaxed">{uc.sales_hook}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active signals */}
        {topTriggers.length > 0 && (
          <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm">
            <div className="px-5 py-3.5 border-b border-app-line flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />
              <h3 className="text-sm font-semibold text-app-ink">Active Signals</h3>
              <span className="ml-auto text-xs text-app-muted">
                {triggers.length} active
              </span>
            </div>
            <div className="divide-y divide-app-line">
              {topTriggers.map(trigger => (
                <div key={trigger.id} className="px-5 py-3.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-app-ink truncate">
                        {trigger.title}
                      </p>
                      {trigger.why_now && (
                        <p className="text-xs text-app-muted mt-0.5 leading-relaxed line-clamp-2">
                          {trigger.why_now}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${
                        trigger.confidence === 'high'
                          ? 'bg-forest-50 text-forest-700'
                          : trigger.confidence === 'medium'
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-app-canvas text-app-muted'
                      }`}>
                        {trigger.confidence}
                      </span>
                      {trigger.urgency_score != null && (
                        <span className="text-[10px] text-app-muted">
                          urgency {trigger.urgency_score}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No enrichment yet */}
        {!hasContext && account && (
          <div className="bg-app-canvas rounded-xl border border-dashed border-app-line px-5 py-6 text-center">
            <p className="text-xs text-app-muted">
              Run account enrichment from the{' '}
              <Link
                to={`/app/accounts/${account.id}`}
                className="text-forest-600 hover:underline"
              >
                account page
              </Link>{' '}
              to surface hypothesis, use case, and signal context here.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
